<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import EmpresaRow from './EmpresaRow.vue';
import CreateEmpresaModal from './CreateEmpresaModal.vue';

const store = useStore();
const router = useRouter();
const { t } = useI18n();

const searchQuery = ref('');
const createModalRef = ref(null);

const empresas = computed(() => store.getters.getEmpresas);
const uiFlags = computed(() => store.getters.getEmpresaUIFlags);

const filteredEmpresas = computed(() => {
  const q = searchQuery.value.trim().toLowerCase();
  if (!q) return empresas.value;
  return empresas.value.filter(c =>
    [c.name, c.email_domain, c.website].some(v =>
      (v || '').toLowerCase().includes(q)
    )
  );
});

const isEmpty = computed(
  () => !uiFlags.value.isFetching && !filteredEmpresas.value.length
);

const loadEmpresas = () => store.dispatch('fetchEmpresas');

const openEmpresa = empresa =>
  router.push({ name: 'empresa_details', params: { empresaId: empresa.id } });

const onCreated = empresa => openEmpresa(empresa);

onMounted(loadEmpresas);
</script>

<template>
  <div class="flex flex-col w-full h-full bg-n-background">
    <!-- Toolbar -->
    <header
      class="flex items-center justify-between gap-3 px-6 pt-5 pb-3 border-b shrink-0 border-n-weak"
    >
      <h1 class="flex items-center gap-2 text-xl font-semibold text-n-slate-12">
        {{ t('EMPRESAS.HEADER_TITLE') }}
        <span class="text-base font-normal text-n-slate-10">
          {{ empresas.length }}
        </span>
      </h1>
      <div class="flex items-center gap-2">
        <div class="relative w-52 max-w-full">
          <span
            class="absolute -translate-y-1/2 i-lucide-search left-2.5 top-1/2 text-n-slate-10 size-4"
          />
          <input
            v-model="searchQuery"
            type="text"
            :placeholder="t('EMPRESAS.SEARCH_PLACEHOLDER')"
            class="w-full h-8 pl-8 pr-3 text-sm border-none rounded-lg reset-base outline-1 outline outline-n-weak bg-n-alpha-black2 text-n-slate-12 placeholder:text-n-slate-10 focus:outline-n-brand"
          />
        </div>
        <Button
          :label="t('EMPRESAS.ACTIONS.NEW')"
          icon="i-lucide-plus"
          size="sm"
          color="blue"
          @click="createModalRef?.open()"
        />
      </div>
    </header>

    <!-- Body -->
    <section class="flex-1 min-h-0 overflow-y-auto">
      <!-- Loading skeleton -->
      <div
        v-if="uiFlags.isFetching && !empresas.length"
        class="divide-y divide-n-weak/60"
      >
        <div
          v-for="n in 6"
          :key="n"
          class="flex items-center gap-3 px-6 py-3 animate-pulse"
        >
          <span class="rounded-xl size-9 bg-n-alpha-2 shrink-0" />
          <div class="flex flex-col gap-1.5">
            <span class="h-3 rounded w-36 bg-n-alpha-2" />
            <span class="h-2.5 rounded w-24 bg-n-alpha-1" />
          </div>
        </div>
      </div>

      <!-- Empty state -->
      <div
        v-else-if="isEmpty"
        class="flex flex-col items-center justify-center h-full gap-4 px-6 text-center"
      >
        <span
          class="flex items-center justify-center rounded-2xl size-14 bg-n-alpha-1 text-n-slate-10"
        >
          <span class="i-lucide-building-2 size-7" />
        </span>
        <div class="flex flex-col gap-1">
          <p class="text-base font-medium text-n-slate-12">
            {{
              searchQuery ? t('EMPRESAS.NO_RESULTS') : t('EMPRESAS.EMPTY_TITLE')
            }}
          </p>
          <p class="max-w-sm text-sm text-n-slate-10">
            {{
              searchQuery ? t('EMPRESAS.NO_RESULTS_HINT') : t('EMPRESAS.EMPTY')
            }}
          </p>
        </div>
        <Button
          v-if="!searchQuery"
          :label="t('EMPRESAS.ACTIONS.NEW')"
          icon="i-lucide-plus"
          size="sm"
          color="blue"
          @click="createModalRef?.open()"
        />
      </div>

      <!-- List -->
      <div v-else class="divide-y divide-n-weak/60">
        <EmpresaRow
          v-for="empresa in filteredEmpresas"
          :key="empresa.id"
          :empresa="empresa"
          @select="openEmpresa"
        />
      </div>
    </section>

    <CreateEmpresaModal ref="createModalRef" @created="onCreated" />
  </div>
</template>
